'use strict';
// Zentira — composants JS vanille.
// Compilé depuis les Design Components (React) — même logique, sans librairie.
//
// Règles héritées du BRIEF :
//   • IntersectionObserver pour tous les reveals, jamais de listener scroll.
//   • prefers-reduced-motion réduit tout à un fondu.
//   • Gestes : écouter pointerup + pointercancel, jamais mouseup seul.
//   • Jamais de catch vide.
//   • linear et ease nus interdits — les courbes sont les variables CSS.
(() => {

  /* ─────────────────────────────────────────────────────────────
     Utilitaires
  ───────────────────────────────────────────────────────────── */
  const $ = (s, r = document) => r.querySelector(s);
  const $$ = (s, r = document) => [...r.querySelectorAll(s)];
  const on = (el, ev, fn, opts) => el && el.addEventListener(ev, fn, opts);

  function reduced() {
    try { return matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch (_) { return false; }
  }

  /* ─────────────────────────────────────────────────────────────
     zt-rise — IntersectionObserver reveals
     Usage : data-rise sur le conteneur, les enfants directs s'animent
     en stagger 60 ms.
  ───────────────────────────────────────────────────────────── */
  function initReveals() {
    if (!window.IntersectionObserver) return;
    $$('[data-rise]').forEach(g => {
      [...g.children].forEach(k => { k.style.opacity = '0'; });
      const io = new IntersectionObserver(([entry]) => {
        if (!entry.isIntersecting) return;
        io.disconnect();
        const r = reduced();
        [...entry.target.children].forEach((k, i) => {
          k.style.animation = 'none';
          void k.offsetWidth; // reflow — déclenche la transition depuis l'état initial
          k.style.animation = `ztRise var(--zt-t-base) var(--zt-e-smooth) ${r ? 0 : i * 60}ms both`;
        });
      }, { threshold: 0.16 });
      io.observe(g);
      // Repli : si l'élément est au-dessus du fold, il n'intersecte jamais → démasquer après 2,6 s.
      setTimeout(() => [...g.children].forEach(k => { k.style.opacity = ''; }), 2600);
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Bandeau d'annonces tournantes
     Structure HTML attendue : plusieurs [data-ann] frères.
     Le JS les fait défiler toutes les 7 s avec un fondu de 600 ms.
  ───────────────────────────────────────────────────────────── */
  function initAnnouncement() {
    const items = $$('[data-ann]');
    if (!items.length) return;
    // Tous cachés sauf le premier.
    items.forEach((it, i) => {
      it.style.transition = `opacity var(--zt-t-slow) var(--zt-e-smooth)`;
      if (i === 0) { it.style.opacity = '1'; it.style.position = 'relative'; }
      else { it.style.opacity = '0'; it.style.position = 'absolute'; it.style.inset = '0'; it.style.display = 'flex'; it.style.alignItems = 'center'; it.style.justifyContent = 'center'; }
    });
    if (items.length < 2 || reduced()) return;
    let idx = 0;
    setInterval(() => {
      items[idx].style.opacity = '0';
      const next = (idx + 1) % items.length;
      setTimeout(() => {
        items[idx].style.position = 'absolute';
        items[next].style.position = 'relative';
        items[next].style.opacity = '1';
        idx = next;
      }, 620);
    }, 7000);
  }

  /* ─────────────────────────────────────────────────────────────
     Header sticky — ombre à l'apparition
     IntersectionObserver sur #zt-sticky-sentinel (juste avant le header).
  ───────────────────────────────────────────────────────────── */
  function initStickyHeader() {
    const sentinel = $('#zt-sticky-sentinel');
    const header = $('[data-zt-header]');
    if (!sentinel || !header || !window.IntersectionObserver) return;
    new IntersectionObserver(([e]) => {
      header.style.boxShadow = e.isIntersecting
        ? 'none'
        : '0 1px 12px -2px rgba(80,30,15,.14)';
    }).observe(sentinel);
  }

  /* ─────────────────────────────────────────────────────────────
     Focus trap — utilisé par les panneaux modaux
  ───────────────────────────────────────────────────────────── */
  function trapFocus(panel, e) {
    if (e.key !== 'Tab') return;
    const focusable = [...panel.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])'
    )];
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ─────────────────────────────────────────────────────────────
     Gestion du scroll du body
  ───────────────────────────────────────────────────────────── */
  let _scrollY = 0;
  let openCount = 0;
  function lockScroll() {
    _scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
  }
  function unlockScroll() {
    document.body.style.overflow = '';
    window.scrollTo(0, _scrollY);
  }

  /* ─────────────────────────────────────────────────────────────
     Fabrique de panneau latéral (menu mobile + cart drawer)
     dir : 'left' | 'right'
  ───────────────────────────────────────────────────────────── */
  function makePanel({ panel, overlay, onOpen, onClose }) {
    let open = false;

    function show() {
      if (open) return;
      open = true;
      openCount++;
      if (openCount === 1) lockScroll();

      overlay.removeAttribute('hidden');
      panel.removeAttribute('hidden');
      void overlay.offsetWidth; // reflow — nécessaire pour que la transition parte de 0
      overlay.classList.add('zt-open');
      panel.classList.add('zt-open');
      panel.removeAttribute('aria-hidden');
      try { onOpen?.(); } catch (err) { console.error('[Zentira] panel onOpen:', err); }
    }

    function hide() {
      if (!open) return;
      open = false;
      openCount--;
      overlay.classList.remove('zt-open');
      panel.classList.remove('zt-open');
      panel.setAttribute('aria-hidden', 'true');

      const dur = reduced() ? 0 : 350;
      setTimeout(() => {
        overlay.setAttribute('hidden', '');
        panel.setAttribute('hidden', '');
        if (openCount === 0) unlockScroll();
      }, dur);
      try { onClose?.(); } catch (err) { console.error('[Zentira] panel onClose:', err); }
    }

    // Fermer en cliquant sur l'overlay
    on(overlay, 'pointerup', e => { if (e.target === overlay) hide(); });
    on(overlay, 'pointercancel', e => { if (e.target === overlay) hide(); });
    // Fermer avec Échap
    on(document, 'keydown', e => { if (e.key === 'Escape' && open) hide(); });
    // Focus trap
    on(panel, 'keydown', e => { if (open) trapFocus(panel, e); });

    return { show, hide, get open() { return open; } };
  }

  /* ─────────────────────────────────────────────────────────────
     Animation zt-pop (badge, coche, totaux)
  ───────────────────────────────────────────────────────────── */
  function pop(el) {
    if (!el) return;
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = 'ztPop var(--zt-t-base) var(--zt-e-bounce) both';
  }

  /* ─────────────────────────────────────────────────────────────
     Menu mobile
  ───────────────────────────────────────────────────────────── */
  function initMobileMenu() {
    const panel = $('#zt-mobile-menu');
    const overlay = $('#zt-mobile-overlay');
    const burger = $('#zt-burger');
    if (!panel || !overlay || !burger) return;

    // Recherche mobile : le header mobile n'a pas d'entrée de recherche (elle
    // n'existe que dans le header desktop, masqué < 990px → parité cassée). On
    // l'injecte en haut du menu burger. Form GET /search natif (pas de CAPTCHA).
    const scrollArea = panel.querySelector('.zt-scroll');
    if (scrollArea && !panel.querySelector('#zt-mobile-search')) {
      const form = document.createElement('form');
      form.id = 'zt-mobile-search';
      form.action = '/search';
      form.method = 'get';
      form.setAttribute('role', 'search');
      form.style.cssText = 'margin:0 0 16px;';
      form.innerHTML =
        '<div style="display:flex;align-items:center;gap:8px;border:1.5px solid #E1DFD9;border-radius:10px;padding:0 12px;background:#fff;">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6B685F" stroke-width="1.6" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>' +
          '<input type="search" name="q" autocomplete="off" aria-label="Rechercher" placeholder="Rechercher…" style="flex:1;min-width:0;border:none;outline:none;background:none;font-family:var(--zt-body);font-size:1rem;color:var(--zt-ink);padding:12px 0;min-height:44px;">' +
        '</div>';
      scrollArea.insertBefore(form, scrollArea.firstChild);
    }

    const mgr = makePanel({
      panel, overlay,
      onOpen() {
        burger.setAttribute('aria-expanded', 'true');
        setTimeout(() => { ($('#zt-mobile-close') || burger).focus(); }, 80);
      },
      onClose() {
        burger.setAttribute('aria-expanded', 'false');
        setTimeout(() => burger.focus(), 80);
      }
    });

    on(burger, 'click', () => mgr.show());
    $$('[data-zt-close-menu]').forEach(b => on(b, 'click', () => mgr.hide()));
  }

  /* ─────────────────────────────────────────────────────────────
     Cart drawer + AJAX
     Structure HTML attendue dans cart-drawer.liquid :
       #zt-cart-panel       ← panneau principal
       #zt-cart-overlay     ← backdrop
       [data-zt-cart-inner] ← contenu remplacé par Section Rendering
       [data-zt-badge]      ← badge(s) compteur
  ───────────────────────────────────────────────────────────── */
  function initCart() {
    const panel = $('#zt-cart-panel');
    const overlay = $('#zt-cart-overlay');
    if (!panel || !overlay) return;

    const mgr = makePanel({
      panel, overlay,
      onOpen() { setTimeout(() => ($('#zt-cart-close') || panel).focus(), 80); }
    });

    // Tous les boutons "ouvrir le panier" (header desktop + mobile)
    on(document, 'click', e => {
      if (e.target.closest('[data-zt-open-cart]')) mgr.show();
    });
    $$('[data-zt-close-cart]').forEach(b => on(b, 'click', () => mgr.hide()));

    /* ── Section Rendering update ── */
    function updateDrawer(html, count) {
      if (!html) return;
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      const newInner = tmp.querySelector('[data-zt-cart-inner]');
      const curInner = panel.querySelector('[data-zt-cart-inner]');
      if (newInner && curInner) {
        curInner.innerHTML = newInner.innerHTML;
      }
      // Mise à jour du compteur dans tous les badges (header desktop + mobile)
      const n = count ?? parseInt(tmp.querySelector('[data-zt-count]')?.textContent ?? '-1');
      if (n >= 0) {
        $$('[data-zt-badge]').forEach(b => {
          b.textContent = n;
          b.style.display = n === 0 ? 'none' : '';
          pop(b);
        });
      } else {
        $$('[data-zt-badge]').forEach(pop);
      }
      // Compteur du titre du tiroir (hors data-zt-cart-inner) — MAJ manuelle
      const drawerCount = panel.querySelector('[data-zt-drawer-count]');
      if (drawerCount && n >= 0) drawerCount.textContent = n > 0 ? ' (' + n + ')' : '';
    }

    /* ── Requête AJAX cart ── */
    async function cartFetch(url, body) {
      // Locale-aware : sur un sous-dossier de marché (/en-us, /en) il faut
      // appeler {root}/cart/... sinon la Section Rendering API re-rend le tiroir
      // dans la locale racine (FR). Shopify.routes.root vaut "/", "/en/" ou
      // "/en-us/" selon le marché → dynamique, aucun chemin en dur.
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const resp = await fetch(root + url.replace(/^\//, ''), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: root + 'cart' })
      });
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`[Zentira] Cart ${url} HTTP ${resp.status}: ${txt}`);
      }
      const data = await resp.json();
      // item_count est présent sur /cart/change.js mais pas sur /cart/add.js
      updateDrawer(data.sections?.['cart-drawer'], data.item_count ?? null);
      return data;
    }

    /* ── Délégation d'événements sur le panneau (remplacé par SRA) ── */
    on(panel, 'click', async e => {
      const dec = e.target.closest('[data-zt-dec]');
      const inc = e.target.closest('[data-zt-inc]');
      const rem = e.target.closest('[data-zt-remove]');
      const up = e.target.closest('[data-zt-upsell]');
      try {
        if (dec) {
          const key = dec.dataset.ztDec;
          const row = dec.closest('[data-item-key]');
          const qty = parseInt(row?.querySelector('[data-qty]')?.textContent ?? '1');
          await cartFetch('/cart/change.js', { id: key, quantity: Math.max(0, qty - 1) });
        } else if (inc) {
          const key = inc.dataset.ztInc;
          const row = inc.closest('[data-item-key]');
          const qty = parseInt(row?.querySelector('[data-qty]')?.textContent ?? '0');
          await cartFetch('/cart/change.js', { id: key, quantity: qty + 1 });
        } else if (rem) {
          await cartFetch('/cart/change.js', { id: rem.dataset.ztRemove, quantity: 0 });
        } else if (up) {
          // Upsell "passer en pack" : ajoute la variante supérieure puis retire la
          // ligne actuelle (add-puis-remove pour éviter un panier vide transitoire).
          up.disabled = true;
          const item = { id: parseInt(up.dataset.newId), quantity: parseInt(up.dataset.qty || '1') };
          if (up.dataset.newPlan) item.selling_plan = parseInt(up.dataset.newPlan);
          await cartFetch('/cart/add.js', { items: [item] });
          await cartFetch('/cart/change.js', { id: up.dataset.oldKey, quantity: 0 });
        }
      } catch (err) {
        console.error(err);
      }
    });

    /* ── Ajout au panier depuis la PDP (CustomEvent) ── */
    // La section product.liquid dispatch 'zt:addToCart' avec { id, quantity, selling_plan? }
    on(document, 'zt:addToCart', async e => {
      const { id, quantity = 1, selling_plan } = e.detail;
      const item = { id, quantity };
      if (selling_plan) item.selling_plan = selling_plan;
      try {
        await cartFetch('/cart/add.js', { items: [item] });
        mgr.show();
      } catch (err) {
        console.error(err);
      }
    });

    /* ── B3 : ajout produit complémentaire / cadeau en un clic (PDP + tiroir) ──
       Délégué sur document → marche pour le bloc PDP ET le tiroir re-rendu par SRA. */
    on(document, 'click', function (e) {
      const add = e.target.closest('[data-zt-add-variant]');
      if (!add || add.disabled) return;
      const id = parseInt(add.dataset.ztAddVariant, 10);
      if (!id) return;
      add.disabled = true;
      document.dispatchEvent(new CustomEvent('zt:addToCart', { detail: { id: id, quantity: 1 } }));
      setTimeout(function () { add.disabled = false; }, 1500);
    });

    return mgr;
  }

  /* ─────────────────────────────────────────────────────────────
     Footer accordion mobile — max-height / scrollHeight (BRIEF §4)
  ───────────────────────────────────────────────────────────── */
  function initFooterAccordion() {
    $$('[data-zt-acc-btn]').forEach(btn => {
      const panelId = btn.getAttribute('aria-controls');
      const accPanel = document.getElementById(panelId);
      if (!accPanel) return;
      on(btn, 'click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        accPanel.style.maxHeight = expanded ? '0px' : accPanel.scrollHeight + 'px';
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Localisation — submit auto au changement de sélecteur
  ───────────────────────────────────────────────────────────── */
  function initLocalization() {
    $$('[data-zt-loc]').forEach(sel => {
      on(sel, 'change', () => {
        try { sel.closest('form')?.submit(); }
        catch (err) { console.error('[Zentira] Localization submit:', err); }
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Page produit — galerie, sélecteur de plan, ATC sticky
  ───────────────────────────────────────────────────────────── */
  function initProductPage() {
    const dataEl = document.getElementById('zt-product-data');
    if (!dataEl) return;

    let productData;
    try { productData = JSON.parse(dataEl.textContent); }
    catch (err) { console.error('[Zentira] product data JSON:', err); return; }
    const variants = productData.variants;
    if (!variants || !variants.length) return;

    const variantInput = document.getElementById('zt-variant-id');
    const planInput = document.getElementById('zt-selling-plan-id');
    const atcBtn = document.getElementById('zt-atc-btn');
    const atcCta = document.getElementById('zt-atc-cta');
    const priceMain = document.getElementById('zt-price-main');
    const priceCompare = document.getElementById('zt-price-compare');
    const pricePerDay = document.getElementById('zt-price-perday');
    const stickyBar = document.getElementById('zt-sticky-atc-bar');
    const stickyVariant = document.getElementById('zt-sticky-variant');
    const stickyPlan = document.getElementById('zt-sticky-plan');
    const stickyPrice = document.getElementById('zt-sticky-price-display');
    const stickyName = document.getElementById('zt-sticky-format-name');

    if (!variantInput) return;

    let activePlanType = 'once';
    // Défaut = 1er flacon (Achat ponctuel). Si l'URL porte ?variant= (ex. lien
    // du quiz vers le format conseillé), on présélectionne cette variante.
    let activeVariantIdx = 0;
    try {
      const vparam = new URLSearchParams(location.search).get('variant');
      if (vparam) {
        const i = variants.findIndex(v => String(v.id) === String(vparam));
        if (i >= 0) activeVariantIdx = i;
      }
    } catch (e) { console.warn('[Zentira] lecture ?variant= échouée:', e); }
    // Formate comme le filtre Liquid `money` de Shopify, via le money_format
    // du marché actif → identique au rendu serveur ET compatible multi-devise
    // (Shopify Markets). Les montants ({{ variant.price }}) sont déjà dans la
    // devise de présentation ; ici on n'applique que le FORMAT.
    const moneyFormat = window.ZentiraMoneyFormat || '${{amount}}';
    function formatMoney(cents) {
      cents = Math.round(cents || 0);
      const whole = (cents % 100) === 0; // money_without_trailing_zeros
      function fmt(precision, thousands, decimal) {
        const num = (cents / 100).toFixed(precision);
        const parts = num.split('.');
        const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return parts[1] ? intPart + decimal + parts[1] : intPart;
      }
      return moneyFormat.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
        switch (name) {
          case 'amount_no_decimals':                       return fmt(0, ',', '.');
          case 'amount_with_comma_separator':              return whole ? fmt(0, '.', ',') : fmt(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':  return fmt(0, '.', ',');
          case 'amount_with_space_separator':              return whole ? fmt(0, ' ', ',') : fmt(2, ' ', ',');
          case 'amount_no_decimals_with_space_separator':  return fmt(0, ' ', '');
          case 'amount_with_apostrophe_separator':         return whole ? fmt(0, "'", '.') : fmt(2, "'", '.');
          default:                                         return whole ? fmt(0, ',', '.') : fmt(2, ',', '.'); // {{amount}}
        }
      });
    }

    function getPriceData() {
      const v = variants[activeVariantIdx];
      if (!v) return null;
      if (activePlanType === 'once' || !v.selling_plan_allocations.length) {
        return { price: v.price, compare: v.compare_at_price, sellingPlanId: null };
      }
      const spa = v.selling_plan_allocations[0];
      return { price: spa.price, compare: v.price, sellingPlanId: spa.selling_plan_id };
    }

    function updateUI() {
      const v = variants[activeVariantIdx];
      if (!v) return;
      const pd = getPriceData();
      if (!pd) return;

      variantInput.value = v.id;
      if (planInput) planInput.value = pd.sellingPlanId ?? '';
      if (stickyVariant) stickyVariant.value = v.id;
      if (stickyPlan) stickyPlan.value = pd.sellingPlanId ?? '';

      if (priceMain) priceMain.textContent = formatMoney(pd.price);

      if (priceCompare) {
        if (pd.compare && pd.compare > pd.price) {
          priceCompare.textContent = formatMoney(pd.compare);
          priceCompare.hidden = false;
        } else {
          priceCompare.hidden = true;
        }
      }

      // ÷ (30 j × nb de flacons) : un flacon = ~30 j d'usage. Diviser par
      // 30 seul faisait croître le coût/jour avec la quantité.
      const flacons = parseInt(v.title, 10) || (activeVariantIdx + 1);
      const perDay = Math.round(pd.price / (30 * flacons));
      const pdSuffix = (pricePerDay && pricePerDay.dataset.perdaySuffix) || '/ jour';
      const pdShort  = (pricePerDay && pricePerDay.dataset.perdayShort)  || '/j';
      if (pricePerDay) {
        pricePerDay.textContent = '≈ ' + formatMoney(perDay) + ' ' + pdSuffix;
      }

      if (stickyName) stickyName.textContent = v.title + ' · ' + (pd.sellingPlanId ? stickyName.dataset.planSub : stickyName.dataset.planOnce);
      if (stickyPrice) stickyPrice.textContent = formatMoney(pd.price) + ' · ≈ ' + formatMoney(perDay) + pdShort;

      // Mise à jour visuelle des boutons format
      $$('[data-zt-format-btn]').forEach(btn => {
        const isActive = parseInt(btn.dataset.variantIdx) === activeVariantIdx
                      && btn.dataset.planType === activePlanType;
        btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        btn.style.borderColor = isActive ? 'var(--zt-coral-btn)' : '#E8E5DF';
        btn.style.background = isActive ? 'rgba(198,55,26,.05)' : 'transparent';
      });

      // CTA dynamique : "Ajouter au panier" ↔ "S'abonner et économiser 15 %" (fondu)
      const ctaIsSub = activePlanType === 'sub' && pd.sellingPlanId;
      [atcBtn, atcCta, document.getElementById('zt-sticky-atc')].forEach(function (btn) {
        if (!btn || btn.disabled) return;
        const label = ctaIsSub ? (btn.dataset.ctaSub || btn.dataset.label) : (btn.dataset.ctaOnce || btn.dataset.label);
        if (!label) return;
        btn.dataset.label = label;
        if (btn.textContent.trim() === label) return;
        btn.style.opacity = '0';
        setTimeout(function () { btn.textContent = label; btn.style.opacity = '1'; }, 160);
      });
    }

    function setPlan(planType) {
      activePlanType = planType;
      const subPanel = document.getElementById('zt-plan-sub-panel');
      const oncePanel = document.getElementById('zt-plan-once-panel');
      const subCard = document.getElementById('zt-plan-sub');
      const onceCard = document.getElementById('zt-plan-once');
      const subBtn = document.getElementById('zt-plan-sub-btn');
      const onceBtn = document.getElementById('zt-plan-once-btn');
      const subDot = subBtn && subBtn.querySelector('.zt-plan-radio__dot');
      const onceDot = onceBtn && onceBtn.querySelector('.zt-plan-radio__dot');
      const subRadio = subBtn && subBtn.querySelector('.zt-plan-radio');
      const onceRadio = onceBtn && onceBtn.querySelector('.zt-plan-radio');

      if (planType === 'sub') {
        if (subPanel) subPanel.style.maxHeight = subPanel.scrollHeight + 'px';
        if (oncePanel) oncePanel.style.maxHeight = '0px';
        if (subBtn) subBtn.setAttribute('aria-expanded', 'true');
        if (onceBtn) onceBtn.setAttribute('aria-expanded', 'false');
        if (subCard) subCard.style.borderColor = 'var(--zt-coral-btn)';
        if (onceCard) onceCard.style.borderColor = '#E8E5DF';
        if (subRadio) subRadio.style.borderColor = 'var(--zt-coral-btn)';
        if (onceRadio) onceRadio.style.borderColor = '#E8E5DF';
        if (subDot) subDot.style.background = 'var(--zt-coral-btn)';
        if (onceDot) onceDot.style.background = 'transparent';
      } else {
        if (subPanel) subPanel.style.maxHeight = '0px';
        if (oncePanel) oncePanel.style.maxHeight = oncePanel.scrollHeight + 'px';
        if (subBtn) subBtn.setAttribute('aria-expanded', 'false');
        if (onceBtn) onceBtn.setAttribute('aria-expanded', 'true');
        if (subCard) subCard.style.borderColor = '#E8E5DF';
        if (onceCard) onceCard.style.borderColor = 'var(--zt-coral-btn)';
        if (subRadio) subRadio.style.borderColor = '#E8E5DF';
        if (onceRadio) onceRadio.style.borderColor = 'var(--zt-coral-btn)';
        if (subDot) subDot.style.background = 'transparent';
        if (onceDot) onceDot.style.background = 'var(--zt-coral-btn)';
      }
      updateUI();
    }

    // Clicks plans
    on(document.getElementById('zt-plan-sub-btn'), 'click', () => setPlan('sub'));
    on(document.getElementById('zt-plan-once-btn'), 'click', () => setPlan('once'));

    // Clicks formats
    $$('[data-zt-format-btn]').forEach(btn => {
      on(btn, 'click', () => {
        const idx = parseInt(btn.dataset.variantIdx);
        const pt = btn.dataset.planType;
        activeVariantIdx = idx;
        if (pt !== activePlanType) { setPlan(pt); }
        else { updateUI(); }
      });
    });

    // ATC principal + CTA
    function dispatchATC() {
      const id = parseInt(variantInput.value);
      const sp = planInput && planInput.value ? parseInt(planInput.value) : undefined;
      if (!id) return;
      document.dispatchEvent(new CustomEvent('zt:addToCart', {
        detail: { id, quantity: 1, selling_plan: sp }
      }));
    }

    if (atcBtn) {
      on(atcBtn, 'click', () => {
        if (atcBtn.disabled) return;
        atcBtn.disabled = true;
        const orig = atcBtn.dataset.label || atcBtn.textContent;
        atcBtn.textContent = '…';
        dispatchATC();
        setTimeout(() => { atcBtn.disabled = false; atcBtn.textContent = orig; }, 1500);
      });
    }

    if (atcCta) {
      on(atcCta, 'click', () => {
        const orig = atcCta.dataset.label || atcCta.textContent;
        atcCta.textContent = '…';
        dispatchATC();
        setTimeout(() => { atcCta.textContent = orig; }, 1500);
      });
    }

    // ATC sticky
    on(document.getElementById('zt-sticky-atc'), 'click', () => {
      const id = parseInt(stickyVariant && stickyVariant.value);
      const sp = stickyPlan && stickyPlan.value ? parseInt(stickyPlan.value) : undefined;
      if (!id) return;
      document.dispatchEvent(new CustomEvent('zt:addToCart', {
        detail: { id, quantity: 1, selling_plan: sp }
      }));
    });

    // Barre sticky — apparaît dès que le bouton ATC principal sort du viewport
    if (stickyBar && atcBtn && window.IntersectionObserver) {
      stickyBar.style.display = 'block'; // rendu uniquement si JS actif
      new IntersectionObserver(([e]) => {
        const visible = !e.isIntersecting;
        stickyBar.style.transform = visible ? 'translateY(0)' : 'translateY(100%)';
        stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
      }, { threshold: 0 }).observe(atcBtn);
    }

    initGallery();
    initBeforeAfter();
    initPdpFaq();
    initSpecsModal();
    initVidStrip();
    initCarnation();
    setPlan('once'); // défaut = Achat ponctuel (brief : plus d'abo présélectionné)
  }

  /* ─────────────────────────────────────────────────────────────
     Galerie produit — swipe + miniatures + prev/next + dots
  ───────────────────────────────────────────────────────────── */
  function initGallery() {
    const gallery = document.getElementById('zt-gallery');
    if (!gallery) return;
    const imgs = $$('[data-gallery-img]', gallery);
    const dots = $$('[data-gallery-dot]', gallery);
    const thumbs = $$('[data-gallery-thumb]');
    const count = imgs.length;
    if (!count) return;

    let current = 0;

    function goto(idx) {
      current = ((idx % count) + count) % count;
      imgs.forEach((img, i) => { img.style.opacity = i === current ? '1' : '0'; });
      dots.forEach((dot, i) => { dot.style.opacity = i === current ? '1' : '0.35'; });
      thumbs.forEach((thumb, i) => {
        const on = i === current;
        thumb.setAttribute('aria-pressed', on ? 'true' : 'false');
        thumb.style.outline = on ? '2px solid var(--zt-coral-btn)' : 'none';
        // Bande de miniatures sur 1 ligne → faire suivre l'active dans le scroll.
        if (on && thumb.parentElement && thumb.parentElement.scrollTo) {
          const strip = thumb.parentElement;
          const target = thumb.offsetLeft - (strip.clientWidth - thumb.clientWidth) / 2;
          strip.scrollTo({ left: Math.max(0, target), behavior: 'smooth' });
        }
      });
    }

    on(gallery.querySelector('[data-gallery-prev]'), 'click', () => goto(current - 1));
    on(gallery.querySelector('[data-gallery-next]'), 'click', () => goto(current + 1));
    dots.forEach((dot, i) => on(dot, 'click', () => goto(i)));
    thumbs.forEach((thumb, i) => on(thumb, 'click', () => goto(i)));

    let startX = 0;
    let dragging = false;
    on(gallery, 'pointerdown', e => { startX = e.clientX; dragging = true; });
    on(gallery, 'pointerup', e => {
      if (!dragging) return;
      dragging = false;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 40) goto(dx < 0 ? current + 1 : current - 1);
    });
    on(gallery, 'pointercancel', () => { dragging = false; });

    goto(0);
  }

  /* ─────────────────────────────────────────────────────────────
     Swap d'image fluide (carnations, avant/après) — crossfade court.
     Précharge d'abord toutes les URLs pour un swap instantané (pas de
     flash de chargement), puis fondu rapide 150 ms.
  ───────────────────────────────────────────────────────────── */
  function ztPreload(urls) {
    urls.forEach(function (u) { if (u) { var im = new Image(); im.src = u; } });
  }
  function ztSmoothSwap(el, url) {
    if (!el || !url || el.getAttribute('src') === url) return;
    if (reduced()) { el.src = url; return; }
    el.style.transition = 'opacity 150ms cubic-bezier(.4,0,.2,1)';
    el.style.opacity = '0.12';
    setTimeout(function () {
      el.src = url;
      requestAnimationFrame(function () { el.style.opacity = '1'; });
    }, 150);
  }

  /* ─────────────────────────────────────────────────────────────
     Curseur avant / après
  ───────────────────────────────────────────────────────────── */
  function initBeforeAfter() {
    const slider = document.getElementById('zt-ba-slider');
    if (!slider) return;
    const handle = slider.querySelector('[data-ba-handle]');
    const clip = slider.querySelector('[data-ba-clip]');
    const hint = slider.querySelector('[data-ba-hint]');
    let dragging = false;

    function setPos(clientX) {
      const rect = slider.getBoundingClientRect();
      const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      if (clip) clip.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
      if (handle) handle.style.left = pct + '%';
    }

    // Drag depuis n'importe où sur le slider (comme la référence)
    on(slider, 'pointerdown', e => {
      dragging = true;
      try { slider.setPointerCapture(e.pointerId); } catch (err) { /* pointeur déjà relâché */ }
      if (hint) hint.style.opacity = '0';
      setPos(e.clientX);
      e.preventDefault();
    });
    on(slider, 'pointermove', e => { if (dragging) setPos(e.clientX); });
    on(slider, 'pointerup', () => { dragging = false; });
    on(slider, 'pointercancel', () => { dragging = false; });
    // Position initiale au centre
    const rect = slider.getBoundingClientRect();
    setPos(rect.left + rect.width / 2);

    // Sélecteur de carnation → swap fluide des images du slider
    const beforeImg = slider.querySelector('[data-ba-before]');
    const afterImg = slider.querySelector('img[data-ba-after]');
    const chips = $$('[data-ba-tone]');
    const baUrls = [];
    chips.forEach(function (c) { if (c.dataset.before) baUrls.push(c.dataset.before); if (c.dataset.after) baUrls.push(c.dataset.after); });
    ztPreload(baUrls);
    chips.forEach(function (chip) {
      on(chip, 'click', function () {
        ztSmoothSwap(beforeImg, chip.dataset.before);
        ztSmoothSwap(afterImg, chip.dataset.after);
        chips.forEach(function (c) {
          const active = c === chip;
          c.setAttribute('aria-checked', active ? 'true' : 'false');
          c.style.backgroundColor = active ? '#FFF1EC' : '#fff';
          c.style.borderColor = active ? '#C6371A' : '#E1DFD9';
          const lbl = c.querySelector('span:last-child');
          if (lbl) lbl.style.fontWeight = active ? '700' : '500';
        });
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     FAQ PDP — chevron animé sur <details>
  ───────────────────────────────────────────────────────────── */
  function initPdpFaq() {
    $$('.zt-faq-item').forEach(det => {
      const chevron = det.querySelector('.zt-faq-chevron');
      if (!chevron) return;
      on(det, 'toggle', () => {
        chevron.style.transform = det.open ? 'rotate(180deg)' : 'rotate(0deg)';
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Modale "Informations produit" (zt-specs-modal)
  ───────────────────────────────────────────────────────────── */
  function initSpecsModal() {
    const modal = document.getElementById('zt-specs-modal');
    const openBtn = document.getElementById('zt-specs-btn');
    const closeBtn = document.getElementById('zt-specs-close');
    if (!modal || !openBtn) return;

    function openSpecs() {
      modal.classList.add('zt-modal--open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }
    function closeSpecs() {
      modal.classList.remove('zt-modal--open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      openBtn.focus();
    }

    on(openBtn, 'click', openSpecs);
    if (closeBtn) on(closeBtn, 'click', closeSpecs);
    on(modal, 'click', e => { if (e.target === modal) closeSpecs(); });
    on(document, 'keydown', e => {
      if (e.key === 'Escape' && modal.classList.contains('zt-modal--open')) closeSpecs();
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Bandeau vidéo (zt-vid-strip-outer) + modale (zt-vid-modal)
  ───────────────────────────────────────────────────────────── */
  function initVidStrip() {
    // Drag-scroll souris/pen + molette + tactile natifs sur les 2 carrousels.
    // Le clic sur une carte [data-zt-video] ouvre la visionneuse (initVideoViewer) ;
    // on empêche seulement le clic de se déclencher juste après un drag.
    ['zt-vid-strip-outer', 'zt-vid-wall-outer'].forEach(function (id) {
      const el = document.getElementById(id);
      if (!el) return;
      let down = false, x0 = 0, sl0 = 0, moved = false;
      el.style.cursor = 'grab';
      on(el, 'pointerdown', function (e) {
        if (e.pointerType === 'touch') return; // tactile : scroll natif
        down = true; moved = false; x0 = e.clientX; sl0 = el.scrollLeft;
        el.style.cursor = 'grabbing';
      });
      on(window, 'pointermove', function (e) {
        if (!down) return;
        const dx = e.clientX - x0;
        if (Math.abs(dx) > 4) moved = true;
        el.scrollLeft = sl0 - dx;
      });
      function end() { if (!down) return; down = false; el.style.cursor = 'grab'; }
      on(window, 'pointerup', end);
      on(window, 'pointercancel', end);
      el.addEventListener('click', function (e) {
        if (moved) { e.stopPropagation(); e.preventDefault(); moved = false; }
      }, true);
    });
  }

  function initVidStripLegacy_UNUSED() {
    const outer = document.getElementById('zt-vid-strip-outer');
    const track = document.getElementById('zt-vid-strip-track');
    const modal = document.getElementById('zt-vid-modal');
    const panel = document.getElementById('zt-vid-panel');
    const tagEl = document.getElementById('zt-vid-tag');
    const dotsEl = document.getElementById('zt-vid-dots');
    const prevBtn = document.getElementById('zt-vid-prev');
    const nextBtn = document.getElementById('zt-vid-next');
    const closeBtn = document.getElementById('zt-vid-close');
    if (!outer || !track || !modal) return;

    const VIDS = [
      { tag: '@lina.mp · le moussage',         bg: 'linear-gradient(160deg,#7A4A32,#3A2318)' },
      { tag: '@sofiabeauty · avant / après', bg: 'linear-gradient(160deg,#B07E4E,#5A3520)' },
      { tag: '@camille.k · routine du matin',   bg: 'linear-gradient(160deg,#D9A870,#7A4A32)' },
      { tag: '@yasmine.d · première fois',      bg: 'linear-gradient(160deg,#6E3F28,#2C1A10)' },
      { tag: '@mei.lin · texture en macro',     bg: 'linear-gradient(160deg,#E8BE9C,#A06B42)' },
      { tag: '@noor.b · une semaine après',     bg: 'linear-gradient(160deg,#8A5A3C,#3A2318)' },
    ];
    const N = VIDS.length;
    const CARD_W = 122; // 112px + 10px gap
    const loopW = CARD_W * N;
    let offset = 0, raf = null, held = false, dragMoved = false, curVid = 0, lastOpenBtn = null;
    let last = performance.now();
    const reduce = reduced();

    // Build dots
    if (dotsEl) {
      VIDS.forEach((_, i) => {
        const d = document.createElement('span');
        d.dataset.vidDot = i;
        d.style.cssText = 'display:inline-block;height:5px;border-radius:3px;transition:width var(--zt-t-base) var(--zt-e-smooth),background var(--zt-t-base) var(--zt-e-smooth);';
        dotsEl.appendChild(d);
      });
    }

    function updateDots() {
      if (!dotsEl) return;
      dotsEl.querySelectorAll('[data-vid-dot]').forEach((d, i) => {
        d.style.width = i === curVid ? '24px' : '5px';
        d.style.background = i === curVid ? 'rgba(253,252,250,.86)' : 'rgba(253,252,250,.35)';
      });
    }

    function openVid(idx, triggerEl) {
      curVid = ((idx % N) + N) % N;
      lastOpenBtn = triggerEl || null;
      const v = VIDS[curVid];
      if (panel) panel.style.background = v.bg;
      if (tagEl) tagEl.textContent = v.tag;
      updateDots();
      modal.classList.add('zt-modal--open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (closeBtn) closeBtn.focus();
    }

    function closeVid() {
      modal.classList.remove('zt-modal--open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastOpenBtn) lastOpenBtn.focus();
    }

    if (prevBtn) on(prevBtn, 'click', () => openVid(curVid - 1));
    if (nextBtn) on(nextBtn, 'click', () => openVid(curVid + 1));
    if (closeBtn) on(closeBtn, 'click', closeVid);
    on(modal, 'click', e => { if (e.target === modal) closeVid(); });
    on(document, 'keydown', e => {
      if (!modal.classList.contains('zt-modal--open')) return;
      if (e.key === 'Escape') { closeVid(); return; }
      if (e.key === 'ArrowLeft') openVid(curVid - 1);
      if (e.key === 'ArrowRight') openVid(curVid + 1);
    });

    // Strip continuous scroll via rAF
    function stepStrip(now) {
      const dt = Math.min(now - last, 80);
      last = now;
      if (!held) {
        offset -= dt * 0.018;
        if (-offset >= loopW) offset += loopW;
        track.style.transform = 'translateX(' + offset + 'px)';
      }
      raf = requestAnimationFrame(stepStrip);
    }
    if (!reduce) raf = requestAnimationFrame(stepStrip);

    // Strip drag
    on(outer, 'pointerdown', e => {
      const x0 = e.clientX, o0 = offset;
      held = true;
      dragMoved = false;
      function move(ev) {
        if (Math.abs(ev.clientX - x0) > 4) dragMoved = true;
        offset = o0 + (ev.clientX - x0);
        track.style.transform = 'translateX(' + offset + 'px)';
      }
      function up() {
        held = false;
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', up);
        if (!raf && !reduce) { last = performance.now(); raf = requestAnimationFrame(stepStrip); }
      }
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', up);
    });

    // Click on vid cards → open modal
    $$('.zt-vid-card').forEach(btn => {
      on(btn, 'click', function() {
        if (dragMoved) return;
        const idx = parseInt(this.dataset.vidIdx || '0', 10);
        openVid(idx, this);
      });
    });
  }

  /* ─────────────────────────────────────────────────────────────
     Sélecteur de carnation §5 (zt-tone-group) — change les images
     des étapes et la photo du problème selon la teinte choisie.
  ───────────────────────────────────────────────────────────── */
  function initCarnation() {
    const group = document.getElementById('zt-tone-group');
    if (!group) return;

    // Les URLs des images par teinte vivent dans les data attributes
    // (data-claire / data-mate / data-metisse / data-foncee) posés par
    // product.liquid via asset_url — jamais de CDN externe en dur ici.
    const TONE_KEYS = ['claire', 'mate', 'metisse', 'foncee'];

    const chips = $$('[data-tone]', group);
    const mechImgs = $$('[data-mech-img]');
    const probImg = document.querySelector('[data-prob-img]');
    const reduceMotion = reduced();

    // Précharge toutes les images de teinte (mécanisme + problème) → swap instantané
    const TONE_URLS = [];
    [...mechImgs, probImg].forEach(function (el) {
      if (!el) return;
      TONE_KEYS.forEach(function (k) { if (el.dataset[k]) TONE_URLS.push(el.dataset[k]); });
    });
    ztPreload(TONE_URLS);

    function swapImg(el, url) { ztSmoothSwap(el, url); }

    function setTone(idx) {
      const key = TONE_KEYS[idx];
      if (!key) return;

      chips.forEach((chip, i) => {
        const active = i === idx;
        chip.setAttribute('aria-checked', active ? 'true' : 'false');
        const label = chip.querySelector('span:last-child');
        if (label) {
          label.style.fontWeight = active ? '700' : '400';
          label.style.color = active ? '#F4F2EE' : '#A8A196';
        }
      });

      mechImgs.forEach((img) => {
        const url = img.dataset[key];
        if (url) swapImg(img, url);
      });
      if (probImg && probImg.dataset[key]) swapImg(probImg, probImg.dataset[key]);
    }

    let toneIdx = 3; // Foncée par défaut
    let toneHeld = false;
    let toneIv = null;

    chips.forEach((chip, i) => {
      on(chip, 'click', () => {
        toneHeld = true;
        if (toneIv) { clearInterval(toneIv); toneIv = null; }
        toneIdx = i;
        setTone(i);
      });
    });

    // Auto-rotation toutes les 5 s si l'utilisateur n'a pas interagi
    if (!reduceMotion) {
      toneIv = setInterval(() => {
        if (toneHeld) return;
        toneIdx = (toneIdx + 1) % TONE_KEYS.length;
        setTone(toneIdx);
      }, 5000);
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Header nav — regroupe les liens secondaires dans un menu « Plus »
     Enrichissement progressif côté client : le sections/header.liquid du
     thème live ne porte pas (encore) le dropdown — sa synchro GitHub est
     bloquée. On l'ajoute ici, depuis un asset qui, lui, se déploie
     normalement. Si le header Liquid finit par fournir le dropdown, on
     s'efface (garde d'idempotence). Défaut : 3 liens visibles, reste en
     « Plus ». Dégradation gracieuse : sans JS, tous les liens restent
     accessibles en clair.
  ───────────────────────────────────────────────────────────── */
  function initHeaderNav() {
    if (document.getElementById('zt-nav-more-btn')) return; // déjà fourni par Liquid
    const nav = $('.zt-hdr-desktop nav');
    if (!nav) return;
    const links = $$(':scope > a.zt-hdr-nav-link', nav);
    const VISIBLE = 3;
    if (links.length <= VISIBLE + 1) return; // 4 liens ou moins : on laisse tout

    const extra = links.slice(VISIBLE);

    const wrap = document.createElement('div');
    wrap.style.cssText = 'position:relative;';

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.id = 'zt-nav-more-btn';
    btn.className = 'zt-hdr-nav-link'; // hérite l'adaptation de couleur (hero/solid)
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-controls', 'zt-nav-more-panel');
    btn.style.cssText = 'display:inline-flex;align-items:center;gap:5px;font-size:.94rem;font-family:inherit;padding:5px 0;background:none;border:none;cursor:pointer;';
    btn.innerHTML = 'Plus <svg id="zt-nav-more-chevron" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="transition:transform var(--zt-t-base,350ms) cubic-bezier(.4,0,.2,1);"><path d="M6 9l6 6 6-6"/></svg>';

    const panel = document.createElement('div');
    panel.id = 'zt-nav-more-panel';
    panel.hidden = true;
    panel.style.cssText = 'position:absolute;top:calc(100% + 10px);left:-14px;z-index:80;min-width:212px;background:var(--zt-canvas,#FAF9F7);border:1px solid #E1DFD9;border-radius:12px;box-shadow:0 16px 44px -12px rgba(22,19,14,.22);padding:8px;';

    extra.forEach((a) => {
      a.classList.remove('zt-draw');
      a.style.cssText = 'display:block;font-size:.92rem;color:#3B3934;text-decoration:none;padding:10px 14px;border-radius:8px;transition:background var(--zt-t-micro,150ms) cubic-bezier(.4,0,.2,1),color var(--zt-t-micro,150ms) cubic-bezier(.4,0,.2,1);';
      on(a, 'mouseenter', () => { a.style.background = '#F0EEE9'; a.style.color = 'var(--zt-ink,#16130E)'; });
      on(a, 'mouseleave', () => { a.style.background = ''; a.style.color = '#3B3934'; });
      panel.appendChild(a);
    });

    wrap.appendChild(btn);
    wrap.appendChild(panel);
    nav.appendChild(wrap);

    const chevron = $('#zt-nav-more-chevron', btn);
    const close = () => { panel.hidden = true; btn.setAttribute('aria-expanded', 'false'); if (chevron) chevron.style.transform = ''; };
    const open = () => { panel.hidden = false; btn.setAttribute('aria-expanded', 'true'); if (chevron) chevron.style.transform = 'rotate(180deg)'; };
    on(btn, 'click', (e) => { e.stopPropagation(); if (panel.hidden) open(); else close(); });
    on(document, 'click', (e) => { if (!panel.hidden && !btn.contains(e.target) && !panel.contains(e.target)) close(); });
    on(document, 'keydown', (e) => { if (!panel.hidden && (e.key === 'Escape' || e.key === 'Esc')) { close(); btn.focus(); } });
    on(panel, 'click', (e) => { if (e.target.closest('a')) close(); });
  }

  /* ─────────────────────────────────────────────────────────────
     Visionneuse vidéo plein écran (format TikTok) — réutilisable.
     Toute carte [data-zt-video] (contenant <video><source>) l'ouvre :
     clic → plein écran + son, swipe/molette/flèches → suivante/précédente.
  ───────────────────────────────────────────────────────────── */
  function initVideoViewer() {
    const overlay = document.getElementById('zt-video-viewer');
    if (!overlay) return;
    const vid = overlay.querySelector('[data-viewer-video]');
    const stage = overlay.querySelector('[data-viewer-stage]');
    const hint = overlay.querySelector('[data-viewer-hint]');
    const counter = overlay.querySelector('[data-viewer-count]');
    const prevBtn = overlay.querySelector('[data-viewer-prev]');
    const nextBtn = overlay.querySelector('[data-viewer-next]');
    const muteBtn = overlay.querySelector('[data-viewer-mute]');
    const muteOn = overlay.querySelector('[data-mute-on]');
    const muteOff = overlay.querySelector('[data-mute-off]');
    if (!vid || !stage) return;

    let list = [], idx = 0, hintShown = false;

    function cards() { return $$('[data-zt-video]'); }
    function srcOf(card) {
      const v = card.querySelector('video');
      if (!v) return null;
      const s = v.querySelector('source');
      const src = (s && s.getAttribute('src')) || v.currentSrc || v.getAttribute('src');
      return src ? { src: src, poster: v.getAttribute('poster') || '' } : null;
    }
    function reflectMute() {
      if (muteOn) muteOn.style.display = vid.muted ? 'none' : 'block';
      if (muteOff) muteOff.style.display = vid.muted ? 'block' : 'none';
    }
    function render() {
      const item = list[idx];
      if (!item) return;
      if (item.poster) vid.setAttribute('poster', item.poster); else vid.removeAttribute('poster');
      vid.src = item.src;
      vid.currentTime = 0;
      const p = vid.play();
      if (p && p.catch) p.catch(function () {});
      if (counter) counter.textContent = (idx + 1) + ' / ' + list.length;
      const multi = list.length > 1;
      if (prevBtn) prevBtn.style.display = multi ? 'flex' : 'none';
      if (nextBtn) nextBtn.style.display = multi ? 'flex' : 'none';
      reflectMute();
    }
    function showHint() {
      if (hintShown || !hint || list.length < 2) return;
      hintShown = true;
      hint.style.opacity = '1';
      setTimeout(function () { hint.style.opacity = '0'; }, 2600);
    }
    function isOpen() { return overlay.style.display !== 'none' && !overlay.hidden; }
    function open(startIdx) {
      list = cards().map(srcOf).filter(Boolean);
      if (!list.length) return;
      idx = Math.max(0, Math.min(startIdx, list.length - 1));
      overlay.hidden = false;
      overlay.style.display = 'flex';
      document.documentElement.style.overflow = 'hidden';
      vid.muted = false;
      render();
      showHint();
    }
    function close() {
      overlay.style.display = 'none';
      overlay.hidden = true;
      document.documentElement.style.overflow = '';
      vid.pause();
      vid.removeAttribute('src');
      vid.load();
    }
    function go(delta) {
      const n = idx + delta;
      if (n < 0 || n >= list.length) return;
      idx = n;
      render();
    }

    document.addEventListener('click', function (e) {
      const card = e.target.closest('[data-zt-video]');
      if (!card) return;
      e.preventDefault();
      open(cards().indexOf(card));
    });
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') go(-1);
    });
    const closeBtn = overlay.querySelector('[data-viewer-close]');
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (prevBtn) prevBtn.addEventListener('click', function () { go(-1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { go(1); });
    if (muteBtn) muteBtn.addEventListener('click', function () { vid.muted = !vid.muted; if (!vid.muted) { const p = vid.play(); if (p && p.catch) p.catch(function () {}); } reflectMute(); });
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });
    let wheelLock = false;
    overlay.addEventListener('wheel', function (e) {
      if (!isOpen()) return;
      e.preventDefault();
      if (wheelLock) return;
      wheelLock = true;
      setTimeout(function () { wheelLock = false; }, 420);
      go(e.deltaY > 0 ? 1 : -1);
    }, { passive: false });
    let sy = 0, sActive = false;
    stage.addEventListener('touchstart', function (e) { sy = e.touches[0].clientY; sActive = true; }, { passive: true });
    stage.addEventListener('touchend', function (e) {
      if (!sActive) return; sActive = false;
      const dy = e.changedTouches[0].clientY - sy;
      if (Math.abs(dy) > 48) go(dy < 0 ? 1 : -1);
    }, { passive: true });
    stage.addEventListener('touchcancel', function () { sActive = false; });
  }

  /* ─────────────────────────────────────────────────────────────
     Boot
  ───────────────────────────────────────────────────────────── */
  document.addEventListener('DOMContentLoaded', () => {
    initReveals();
    initAnnouncement();
    initStickyHeader();
    initHeaderNav();
    initMobileMenu();
    initCart();
    initFooterAccordion();
    initLocalization();
    initProductPage();
    initVideoViewer();
  });

})();
